import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    Legend,
} from "recharts";
import SkeletonCard from "./common/SkeletonCard";
import EmptyState from "./common/EmptyState";

import DashboardCard from "./common/DashboardCard";

import { API_BASE } from '../api/config';

function PipelineOverview({ filterContext, bundleData, onMaximize, fullScreen = false }) {
    const { salesperson, year, region, displayCurrency = "AED" } = filterContext || {};

    const [rawData, setRawData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const res = await axios.get(`${API_BASE}/api/metrics/pipeline-overview`, {
                params: {
                    salesperson,
                    year,
                    region: region || "all",
                },
            });
            const serverData = res.data?.data || [];
            setRawData(Array.isArray(serverData) ? serverData : []);
        } catch (err) {
            console.error("PipelineOverview error", err);
            setError(err.message || "Failed to load pipeline overview");
        } finally {
            setLoading(false);
        }
    }, [salesperson, year, region]);

    useEffect(() => {
        if (!salesperson || !year) return;

        if (bundleData && Array.isArray(bundleData.data) && bundleData.data.length > 0) {
            setRawData(bundleData.data);
            setLoading(false);
            setError("");
            return;
        }

        fetchData();
    }, [salesperson, year, region, bundleData, fetchData]);

    const hasData = useMemo(() => Array.isArray(rawData) && rawData.length > 0, [rawData]);

    const canonicalStages = useMemo(() => ([
        "Approved",
        "In Approval",
        "Estimation/BOQ",
        "Negotiation",
        "New",
        "Quote Ready",
        "Quote Sent",
        "Site Survey",
        "Site Survey Completed",
    ]), []);

    const data = useMemo(() => canonicalStages.map((stageName) => {
        const found = rawData.find((row) => row.stage === stageName) || {};
        return {
            stage: stageName,
            deal_count: found.deal_count || 0,
            total_value_aed: found.total_value_aed || 0,
        };
    }), [canonicalStages, rawData]);

    const totals = useMemo(() => {
        return data.reduce((acc, row) => {
            acc.totalDeals += row.deal_count || 0;
            acc.totalValueAed += row.total_value_aed || 0;
            return acc;
        }, { totalDeals: 0, totalValueAed: 0 });
    }, [data]);

    const valueChartData = useMemo(() => data.map((row) => ({
        stage: row.stage,
        value_m_aed: (row.total_value_aed || 0) / 1_000_000,
    })), [data]);

    const countChartData = useMemo(() => data.map((row) => ({
        stage: row.stage,
        deal_count: row.deal_count || 0,
    })), [data]);

    const dataByStage = useMemo(() => data.reduce((acc, row) => {
        acc[row.stage] = row;
        return acc;
    }, {}), [data]);

    const handleMaximize = useCallback(() => {
        if (onMaximize) onMaximize();
    }, [onMaximize]);

    const actionButtons = useMemo(() => (
        <>
            {onMaximize && !fullScreen && (
                <button
                    onClick={handleMaximize}
                    style={{
                        padding: "8px",
                        background: "rgba(255, 255, 255, 0.05)",
                        color: "#e5e7eb",
                        borderRadius: 8,
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.2s ease"
                    }}
                    title="Maximize"
                >
                    <span style={{ fontSize: "16px", lineHeight: 1 }}>⤢</span>
                </button>
            )}
            {fullScreen && onMaximize && (
                <button
                    onClick={handleMaximize}
                    style={{
                        padding: "8px",
                        background: "rgba(255, 107, 53, 0.2)",
                        color: "#ff6b35",
                        border: "1px solid #ff6b35",
                        borderRadius: "50%",
                        width: "36px",
                        height: "36px",
                        cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.2s ease"
                    }}
                >

                </button>
            )}
        </>
    ), [onMaximize, fullScreen, handleMaximize]);

    const subtitle = useMemo(() => (
        <p
            style={{
                color: "#94a3b8",
                fontSize: fullScreen ? 14 : 13,
                margin: "0 0 16px 0",
                textAlign: fullScreen ? "center" : "left",
            }}
        >
            Salesperson: <strong style={{ color: "#e2e8f0" }}>{salesperson}</strong> · Region:{" "}
            <strong style={{ color: "#e2e8f0" }}>{region === "all" ? "All Regions" : region}</strong>
        </p>
    ), [salesperson, region, fullScreen]);

    if (!salesperson || !year) return null;

    return (
        <DashboardCard
            title={`Forecast Pipeline Overview - ${year}`}
            actions={actionButtons}
            fullScreen={fullScreen}
            gradient="primary"
        >
            <div style={{ height: "100%", overflowY: "auto", paddingRight: "4px" }}>
                {subtitle}

                {/* Summary tiles */}
                {hasData && (
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "12px",
                            marginBottom: "20px",
                        }}
                    >
                        {/* Total Open Deals */}
                        <div
                            style={{
                                flex: "1 1 200px",
                                backgroundColor: "rgba(17, 24, 39, 0.6)",
                                borderRadius: 12,
                                padding: "12px 14px",
                                border: "1px solid rgba(255,255,255,0.08)",
                            }}
                        >
                            <div
                                style={{
                                    color: "#94a3b8",
                                    fontSize: 11,
                                    marginBottom: 4,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px"
                                }}
                            >
                                Total Open Deals
                            </div>
                            <div
                                style={{
                                    color: "#ffffff",
                                    fontSize: 20,
                                    fontWeight: "bold",
                                }}
                            >
                                {totals.totalDeals}
                            </div>
                        </div>

                        {/* Total Pipeline Value */}
                        <div
                            style={{
                                flex: "1 1 200px",
                                backgroundColor: "rgba(255, 107, 53, 0.1)",
                                borderRadius: 12,
                                padding: "12px 14px",
                                border: "1px solid rgba(255, 107, 53, 0.2)",
                            }}
                        >
                            <div
                                style={{
                                    color: "#fdba74",
                                    fontSize: 11,
                                    marginBottom: 4,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px"
                                }}
                            >
                                Total Pipeline Value ({displayCurrency})
                            </div>
                            <div
                                style={{
                                    color: "#ff6b35",
                                    fontSize: 20,
                                    fontWeight: "bold",
                                }}
                            >
                                {displayCurrency} {Math.round(totals.totalValueAed).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Stage-wise tiles */}
                {hasData && (
                    <div
                        style={{
                            marginTop: 8,
                            marginBottom: 20,
                        }}
                    >
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                                gap: "12px",
                            }}
                        >
                            {(() => {
                                const tileOrder = [
                                    "Approved",
                                    "In Approval",
                                    "Estimation/BOQ",
                                    "Negotiation",
                                    "_spacer_",
                                    "New",
                                    "Quote Ready",
                                    "Quote Sent",
                                    "Site Survey",
                                    "Site Survey Completed",
                                ];

                                return tileOrder.map((stageName, idx) => {
                                    if (stageName === "_spacer_") {
                                        return (
                                            <div
                                                key={`spacer-${idx}`}
                                                style={{ visibility: "hidden" }}
                                            />
                                        );
                                    }

                                    const row = dataByStage[stageName] || {
                                        stage: stageName,
                                        deal_count: 0,
                                        total_value_aed: 0,
                                    };

                                    return (
                                        <div
                                            key={stageName}
                                            style={{
                                                backgroundColor: "rgba(17, 24, 39, 0.4)",
                                                borderRadius: 12,
                                                padding: "10px 12px",
                                                border: "1px solid rgba(255,255,255,0.05)",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    color: "#94a3b8",
                                                    fontSize: 11,
                                                    marginBottom: 4,
                                                    fontWeight: 600,
                                                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
                                                }}
                                                title={stageName}
                                            >
                                                {stageName}
                                            </div>
                                            <div
                                                style={{
                                                    color: "#ffffff",
                                                    fontSize: 13,
                                                    fontWeight: 500,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                {displayCurrency}{" "}
                                                {Math.round(
                                                    (row.total_value_aed || 0)
                                                ).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                            </div>
                                            <div
                                                style={{
                                                    color: "#6b7280",
                                                    fontSize: 11,
                                                }}
                                            >
                                                Deals:{" "}
                                                <span style={{ fontWeight: 600, color: "#9ca3af" }}>
                                                    {row.deal_count || 0}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </div>
                )}

                {loading && (
                    <SkeletonCard height="400px" />
                )}
                {error && (
                    <EmptyState icon="️" message={error} size="small" />
                )}
                {!loading && !error && !hasData && (
                    <EmptyState
                        icon=""
                        message="No open pipeline stages found for the selected filters."
                        size="medium"
                    />
                )}

                {/* Chart 1: Value by Stage (horizontal bars) */}
                {!loading && !error && hasData && (
                    <div
                        style={{
                            marginTop: fullScreen ? "18px" : "12px",
                            height: fullScreen ? 340 : 300,
                            minHeight: 300,
                        }}
                    >
                        <h3
                            style={{
                                color: "#e5e7eb",
                                margin: "0 0 12px 0",
                                fontSize: fullScreen ? 24 : 20,
                                fontWeight: 600,
                            }}
                        >
                            Pipeline Value by Stage
                        </h3>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={valueChartData}
                                layout="vertical"
                                barCategoryGap="20%"
                                margin={{ top: 10, right: 10, left: 70, bottom: 10 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" horizontal={false} />
                                <XAxis
                                    type="number"
                                    stroke="#94a3b8"
                                    tick={{ fill: "#94a3b8", fontSize: 11 }}
                                    tickFormatter={(v) => `${v.toFixed(1)}M`}
                                    tickMargin={8}
                                    axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                                    tickLine={false}
                                />
                                <YAxis
                                    dataKey="stage"
                                    type="category"
                                    stroke="#94a3b8"
                                    tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 600 }}
                                    width={120}
                                    interval={0}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip
                                    contentStyle={{ backgroundColor: "#1e293b", borderColor: "rgba(255,255,255,0.1)", color: "#f1f5f9" }}
                                    formatter={(value) => [`${displayCurrency} ${Math.round(value * 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, "Value"]}
                                    cursor={{ fill: "rgba(255,255,255,0.05)" }}
                                />
                                <Bar
                                    dataKey="value_m_aed"
                                    name={`Value (Millions ${displayCurrency})`}
                                    fill="#ff6b35"
                                    radius={[0, 4, 4, 0]}
                                    minPointSize={2}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {/* Chart 2: Deal count by stage */}
                {!loading && !error && hasData && (
                    <div
                        style={{
                            marginTop: "24px",
                            height: fullScreen ? 320 : 280,
                            minHeight: 280,
                        }}
                    >
                        <h3
                            style={{
                                color: "#e5e7eb",
                                margin: "0 0 12px 0",
                                fontSize: fullScreen ? 24 : 20,
                                fontWeight: 600,
                            }}
                        >
                            Number of Deals by Stage
                        </h3>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={countChartData}
                                layout="vertical"
                                barCategoryGap="20%"
                                margin={{ top: 10, right: 10, left: 70, bottom: 10 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" horizontal={false} />
                                <XAxis
                                    type="number"
                                    stroke="#94a3b8"
                                    tick={{ fill: "#94a3b8", fontSize: 11 }}
                                    tickMargin={8}
                                    axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                                    tickLine={false}
                                />
                                <YAxis
                                    dataKey="stage"
                                    type="category"
                                    stroke="#94a3b8"
                                    tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 600 }}
                                    width={120}
                                    interval={0}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip
                                    contentStyle={{ backgroundColor: "#1e293b", borderColor: "rgba(255,255,255,0.1)", color: "#f1f5f9" }}
                                    formatter={(value) => [`${value} deals`, "Deals"]}
                                    cursor={{ fill: "rgba(255,255,255,0.05)" }}
                                />
                                <Bar
                                    dataKey="deal_count"
                                    name="No. of Deals"
                                    fill="#10b981"
                                    radius={[0, 4, 4, 0]}
                                    minPointSize={2}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </DashboardCard>
    );
}

export default PipelineOverview;
